import { Link } from "react-router-dom";
import { useState } from "react";
import { ShoppingCart, Search, Receipt, User, LogOut, Sparkles } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { T, useT } from "@/context/TextEditContext";

const LOGO_IMG =
  "https://customer-assets.emergentagent.com/job_mobile-food-app-4/artifacts/isog56bh_Blossom..png";
const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_mobile-food-app-4/artifacts/6sdja2rb_1776885370289.png";

export default function TopBar({ onOpenCart, showSearch = false, onSearch, searchValue = "" }) {
  const { count } = useCart();
  const { user, login, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const searchPlaceholder = useT("nav.search_placeholder", "Search menu...");
  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="sticky top-0 z-30 chalkboard-band border-b-4 border-brand-red" data-testid="top-bar">
      <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
          <div
            className="w-9 h-9 rounded-full bg-cover bg-center border-2 border-brand-yellow shrink-0"
            style={{ backgroundImage: `url(${LOGO_IMG})` }}
          />
          <div className="hidden sm:block">
            <div className="font-display text-brand-yellow text-base leading-none">
              <T k="nav.brand_top" d="Blossom's" />
            </div>
            <div className="font-display text-white/90 text-[10px] leading-none tracking-wider mt-0.5">
              <T k="nav.brand_bottom" d="KITCHEN" />
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-1 sm:gap-2 flex-nowrap">
          <Link
            to="/about"
            data-testid="nav-about-link"
            className="flex items-center min-h-[40px] bg-brand-yellow text-stone-900 px-2.5 sm:px-3 py-2 rounded-full text-[10px] uppercase tracking-normal sm:tracking-widest font-black hover:brightness-105 active:scale-95 transition-all shadow-sm shrink-0"
          >
            <span><T k="nav.about" d="About" /></span>
          </Link>
          <Link
            to="/rewards"
            data-testid="nav-rewards-link"
            className="flex items-center min-h-[40px] bg-brand-yellow text-stone-900 px-2.5 sm:px-3 py-2 rounded-full text-[10px] uppercase tracking-normal sm:tracking-widest font-black hover:brightness-105 active:scale-95 transition-all shadow-sm shrink-0"
          >
            <span><T k="nav.rewards" d="Rewards" /></span>
          </Link>
          <Link
            to="/track"
            data-testid="nav-track-link"
            className="flex items-center gap-1 min-h-[40px] bg-brand-yellow text-stone-900 px-2.5 sm:px-3 py-2 rounded-full text-[10px] uppercase tracking-normal sm:tracking-widest font-black hover:brightness-105 active:scale-95 transition-all shadow-sm shrink-0"
          >
            <Receipt className="w-3 h-3" />
            <span><T k="nav.track" d="Track" /></span>
          </Link>
          <button
            onClick={onOpenCart}
            data-testid="cart-drawer-trigger"
            className="relative bg-brand-red hover:bg-red-800 text-white rounded-full w-10 h-10 flex items-center justify-center transition-transform hover:-translate-y-0.5 active:scale-95 shrink-0"
          >
            <ShoppingCart className="w-[18px] h-[18px]" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1 -right-1 bg-brand-yellow text-stone-900 text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center border-2 border-stone-900"
              >
                {count}
              </span>
            )}
          </button>

          {user ? (
            <div className="relative shrink-0">
              <button
                onClick={() => setMenuOpen((o) => !o)}
                data-testid="nav-account-btn"
                aria-label="Account menu"
                className="w-10 h-10 rounded-full bg-brand-yellow text-stone-900 font-black flex items-center justify-center border-2 border-stone-900 hover:brightness-105 active:scale-95 transition-all overflow-hidden"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <span className="text-xs">{initial}</span>
                )}
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div
                    data-testid="nav-account-menu"
                    className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-stone-200 z-50 overflow-hidden"
                  >
                    <div className="px-4 py-3 border-b border-stone-100">
                      <div className="text-sm font-bold text-stone-900 truncate">{user.name || "Signed in"}</div>
                      <div className="text-xs text-stone-500 truncate" data-testid="nav-account-email">{user.email}</div>
                      {user.loyalty && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-black text-brand-red">
                          <Sparkles className="w-3 h-3" /> {user.loyalty.points} pts
                        </div>
                      )}
                    </div>
                    <Link
                      to="/rewards"
                      onClick={() => setMenuOpen(false)}
                      data-testid="nav-account-rewards"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                    >
                      <Sparkles className="w-4 h-4 text-brand-red" /> My Rewards
                    </Link>
                    <button
                      onClick={() => { setMenuOpen(false); logout(); }}
                      data-testid="nav-signout-btn"
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50 border-t border-stone-100"
                    >
                      <LogOut className="w-4 h-4" /> Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <button
              onClick={login}
              data-testid="nav-signin-btn"
              className="flex items-center justify-center gap-1 min-h-[40px] min-w-[40px] bg-stone-900 text-brand-yellow px-2 sm:px-3 py-2 rounded-full text-[10px] uppercase tracking-normal sm:tracking-widest font-black hover:brightness-125 active:scale-95 transition-all shadow-sm shrink-0"
            >
              <User className="w-4 h-4" />
              <span className="hidden sm:inline"><T k="nav.signin" d="Sign in" /></span>
            </button>
          )}
        </div>
      </div>
      {showSearch && (
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              value={searchValue}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={searchPlaceholder}
              data-testid="menu-search-input"
              className="w-full pl-9 pr-3 py-2 rounded-full bg-stone-800/60 text-white placeholder:text-stone-400 border border-stone-700 outline-none focus:border-brand-yellow text-sm"
              type="search"
              autoComplete="off"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export { HERO_IMG };
